/**
 * A product barcode as the warehouse registers it. Most are 8-14 digit EAN/UPC codes, but some printed
 * codes carry letters (e.g. "PL…"), so the catalog editor's rule is the admin's own: upper-case letters, digits
 * and hyphens, up to 64 characters. The desk's barcode box saves through the product attributes endpoint, which
 * takes digits only; its rule is the one below (SSD-7996).
 */
const PRODUCT_BARCODE = /^[A-Z0-9-]{1,64}$/;

export const PRODUCT_BARCODE_HINT = 'Letters, digits or - (up to 64)';

export const PRODUCT_BARCODE_ERROR = 'A barcode must be 1-64 letters, digits, or hyphens (no spaces).';

/** The scan lookup matches a barcode exactly, so it is stored in one spelling: trimmed and upper case. */
export const normalizeProductBarcode = (text: string): string => text.trim().toUpperCase();

/** Answers on a code already normalised. */
export const isValidProductBarcode = (code: string): boolean => PRODUCT_BARCODE.test(code);

export const isInvalidEditedProductBarcode = (
  barcode: string | undefined,
  originalBarcode: string | undefined,
): boolean => barcode !== originalBarcode && barcode !== undefined && barcode !== '' && !isValidProductBarcode(barcode);

/**
 * The rule the product master's barcode save (POST /product/attributes) applies, copied from the Facade's
 * BarcodeValidator: digits only, 8, 12, 13 or 14 of them (EAN-8, UPC-A, EAN-13, GTIN-14), and a GS1 check digit
 * that adds up. The desk's barcode box (View Orders, Inventory) saves there, so it refuses what the server would,
 * in the server's words, before the save rather than after it (SSD-7996).
 */
export const PRODUCT_ATTRIBUTE_BARCODE_HINT = 'Digits only: 8, 12, 13 or 14 (EAN/UPC), check digit checked';

const PRODUCT_ATTRIBUTE_BARCODE_LENGTHS = [8, 12, 13, 14];

/** GS1 weighting as the server does it: the digit next to the check digit weighs 3, then 1 and 3 alternate leftwards. */
const hasValidCheckDigit = (code: string): boolean => {
  const digits = [...code].map(Number);
  const checkDigit = digits.pop();
  const evenBody = digits.length % 2 === 0;
  const sum = digits.reduce(
    (total, digit, index) => total + digit * (evenBody ? (index % 2 === 0 ? 1 : 3) : index % 2 === 0 ? 3 : 1),
    0,
  );
  return (10 - (sum % 10)) % 10 === checkDigit;
};

/** The server's reason for refusing a code, in its order and wording, or null when it would take it. */
export const productAttributeBarcodeError = (code: string): string | null => {
  const clean = code.trim();
  if (!clean) {
    return 'Barcode is empty';
  }
  if (!/^\d+$/.test(clean)) {
    return 'Barcode must contain only digits';
  }
  if (!PRODUCT_ATTRIBUTE_BARCODE_LENGTHS.includes(clean.length)) {
    return `Barcode must be 8, 12, 13, or 14 digits (got ${clean.length})`;
  }
  if (!hasValidCheckDigit(clean)) {
    return 'Invalid barcode check digit';
  }
  return null;
};
